/**
 * How long a `blob:` URL is kept alive after handing it to the browser.
 *
 * Revoking immediately races the new tab's load (blank page) or the queued
 * download (truncated or empty file), so the URL is released on a timer instead
 * — long enough for the browser to have fetched the bytes, short enough not to
 * pin them for the session.
 */
const REVOKE_DELAY_MS = 60_000;

/**
 * Opens an already-fetched blob (typically a PDF) in a new browser tab.
 *
 * File endpoints require a Bearer JWT, and a plain `<a href>` or
 * `window.open(apiUrl)` cannot attach the Authorization header. Callers fetch
 * the bytes with `apiFetchBlob` and hand the browser a `blob:` URL instead;
 * this helper wraps the create/open/revoke dance that goes with that.
 *
 * Deliberately passes no features string. `noopener` — which `noreferrer`
 * implies — makes `window.open` return null *unconditionally*, so a suppressed
 * pop-up would be indistinguishable from a successful one and callers could
 * never tell the user their file didn't appear. Nulling `opener` on the handle
 * gives the same protection while preserving that signal, and is safe to skip
 * worrying about here anyway: the opened document is a blob we generated
 * ourselves, not untrusted content.
 *
 * @returns true if the tab opened; false if the browser blocked it. Callers
 *          should surface `false` to the user — the work behind the blob has
 *          usually already been committed server-side.
 */
export function openBlobInNewTab(blob: Blob): boolean {
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, "_blank");
  if (opened) opened.opener = null;
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  return Boolean(opened);
}

/**
 * Saves an already-fetched blob to disk under `fileName`, via a synthetic anchor click.
 *
 * The same authorization problem as `openBlobInNewTab`: a plain `<a href>` pointing at an
 * API URL cannot attach the Bearer token, so callers fetch the bytes first and hand the
 * browser a `blob:` URL.
 *
 * Every step here is load-bearing and was arrived at the hard way across the call sites this
 * replaces. The anchor is attached to the document because a detached anchor's click is
 * ignored by some browsers; `rel="noreferrer"` keeps the blob URL out of any referrer; and
 * the revoke is deferred rather than run straight after `click()`, because the browser has
 * only *queued* the download at that point and immediate revocation can race it — losing
 * that race yields a truncated or empty file, not an error.
 */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
}
